/**
 * Forces all LocalizableComponents to provide ComponentLocalizations for the specified languages.
 * If you ever need to add support for a new language, do it here!
 */
export interface Localization<ComponentLocalization> {
  /**
   * English
   */
  en: ComponentLocalization;

  /**
   * German
   */
  de: ComponentLocalization;

  /**
   * Swedish
   */
  sv: ComponentLocalization;

  /**
   * Dutch locale for the Netherlands
   */
  nl: ComponentLocalization;

  /**
   * Italian
   */
  it: ComponentLocalization;

  /**
   * Hungarian
   */
  hu: ComponentLocalization;

  /**
   * Danish
   */
  da: ComponentLocalization;

  /**
   * French
   */
  fr: ComponentLocalization;

  /**
   * Spanish
   */
  es: ComponentLocalization;

  /**
   * Polish
   */
  pl: ComponentLocalization;

  /**
   * Romanian
   */
  ro: ComponentLocalization;

  /**
   * Croatian
   */
  hr: ComponentLocalization;

  /**
   * Czech
   */
  cs: ComponentLocalization;

  /**
   * Slovenian
   */
  sl: ComponentLocalization;
}

/**
 * Keep in sync with the above defined interface. These keys will be checked on runtime by setCurrentLocalizationDelegate() function.
 */
export enum LanguageKeys {
  en,
  de,
  sv,
  nl,
  it,
  hu,
  da,
  fr,
  es,
  pl,
  ro,
  hr,
  cs,
  sl
}

/**
 * Implement this interface if your component has hard-coded localizable strings (i18n).
 * @example See the Link component
 */
export interface LocalizableComponent<ComponentLocalization> {
  /**
   * The host element of the custom component. Should be annotated with the Stencil decorator @Element().
   * @example @Element() hostElement: HTMLEonUiMyComponentElement;
   */
  hostElement;

  /**
   * The localization of the component for all languages.
   */
  localization: Localization<ComponentLocalization>;

  /**
   * The current localization based on the closest lang attribute.
   * Use this to set translated strings in your component.
   */
  currentLocalization: ComponentLocalization;

  /**
   * Determines the current language context of the component and updates the currentLocalization property.
   *
   * @default
   * setCurrentLocalization() {
   *   setCurrentLocalizationDelegate(this);
   * }
   */
  setCurrentLocalization(): void;
}

const languageLocalization = (element: HTMLElement) => {
  const parent = element && ((element.parentNode as HTMLElement) || ((element as any).host as HTMLElement));
  let langAttributeAvailable = parent && typeof parent.hasAttribute === "function" && parent.hasAttribute("lang");

  switch (langAttributeAvailable) {
    case true:
      return parent;
    case false:
      return languageLocalization(parent);
    default:
      break;
  }
};

/**
 * Default implementation for setCurrentLocalization().
 * Determines the current language context of the component based on its own or the nearest enclosing [lang] attribute set on any DOM element.
 * If there is none, it uses "en" (English) as fallback language.
 * Finally updates the currentLocalization property with the appropriate values taken from the localization of the component.
 * @param component A component implementing LocalizableComponent<ComponentLocalization>.
 */
export function setCurrentLocalizationDelegate<ComponentLocalization>(
  component: LocalizableComponent<ComponentLocalization>
): void {
  let closestElementWithLangAttr = component.hostElement.closest("[lang]") as HTMLElement;
  if (closestElementWithLangAttr === null) {
    closestElementWithLangAttr = languageLocalization(component.hostElement);
  }

  let locale = "en"; // fallback language

  if (closestElementWithLangAttr && closestElementWithLangAttr.lang) {
    const lang = closestElementWithLangAttr.lang.indexOf("-")
      ? closestElementWithLangAttr.lang.split("-")[0]
      : closestElementWithLangAttr.lang;

    if (Object.values(LanguageKeys).includes(lang)) locale = lang;
  }

  component.currentLocalization = component.localization[locale];
}

export interface DefaultLocalization {
  required: string;
}
